// src/components/modals/RenameFileModal.tsx
// Updates the display name for a document (what users see in lists)
// S3 filename remains unchanged to preserve uniqueness

import React, { useState, useEffect } from "react";
import { Modal, Box, Button, TextField, CircularProgress } from "@mui/material";
import SectionTitle from "../common/SectionTitle";

interface RenameFileModalProps {
  open: boolean;
  currentDisplayName: string;
  onConfirm: (newDisplayName: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({
  open,
  currentDisplayName,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  // Remove any date suffix like "(03 Feb 2026)" from current display name
  const cleanDisplayName = currentDisplayName.replace(/\s*\(\d{1,2}\s+\w+\s+\d{4}\)$/, '').trim();
  
  const [newDisplayName, setNewDisplayName] = useState(cleanDisplayName);

  useEffect(() => {
    const cleaned = currentDisplayName.replace(/\s*\(\d{1,2}\s+\w+\s+\d{4}\)$/, '').trim();
    setNewDisplayName(cleaned);
  }, [currentDisplayName]);

  const handleConfirm = () => {
    const trimmed = newDisplayName.trim();
    if (!trimmed) { 
      console.error("Display name cannot be empty."); 
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <Modal open={open} onClose={onCancel}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 400 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
        }}
      >
        <SectionTitle title="Rename Document" />
        <TextField
          fullWidth
          label="Display name"
          value={newDisplayName}
          onChange={(e) => setNewDisplayName(e.target.value)}
          helperText="This is the name shown in your document list"
          autoFocus
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleConfirm} 
            disabled={loading || !newDisplayName.trim()}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default RenameFileModal;
